import {PropositionOperation} from "@/ai-assistant/interfaces";

/** What happened to one collaborator's day. */
export type DiffKind = 'ajoute' | 'deplace' | 'retire';

export interface DiffRange {
    starts_at: string;
    ends_at: string;
}

export interface DiffDay {
    memberId: number | null;
    date: string;
    kind: DiffKind;
    /**
     * What the day held, or `null` when the operation did not record it.
     *
     * The two are not the same thing and must not read the same: the decision
     * layer writes `slots: null` on a day it clears, which says the day is
     * being emptied — not that it was empty.
     */
    before: DiffRange[] | null;
    after: DiffRange[];
}

/**
 * Read the ranges an operation carries on one side.
 *
 * `null` slots mean the day is cleared rather than unknown: the decision layer
 * writes it that way, and the overlay removes the day when it sees it.
 */
const ranges = (side: Record<string, unknown> | null | undefined): DiffRange[] | null => {
    const slots = side?.slots;

    return Array.isArray(slots) ? slots as DiffRange[] : null;
};

const dateOf = (operation: PropositionOperation): string =>
    String(operation.after?.date ?? operation.before?.date ?? '');

/**
 * What each operation changes, as two lists of ranges.
 *
 * Nothing is summed here. Telling an added day from a moved one is a
 * comparison of sets, not an arithmetic on hours: a total assembled in the
 * renderer would be a figure the server's guard never saw, and the totals that
 * matter are already served as impacts.
 */
export const propositionDiff = (operations: PropositionOperation[]): DiffDay[] =>
    operations
        .filter(operation => operation.op === 'MODIFIER_SHIFT')
        .map(operation => {
            const before = ranges(operation.before);
            const after = ranges(operation.after) ?? [];

            return {
                memberId: typeof operation.target_id === 'number' ? operation.target_id : null,
                date: dateOf(operation),
                kind: (after.length === 0
                    ? 'retire'
                    : before === null || before.length === 0 ? 'ajoute' : 'deplace') as DiffKind,
                before,
                after
            };
        })
        .sort((first, second) => first.date.localeCompare(second.date));
